import {useCallback, useContext} from "react";
import { useQueryClient } from '@tanstack/react-query';
import useAPI from './useApi';
import { queryKeys } from './useTradeStationQueries';
import { TradingContext } from '../contexts/TradingContext';
import {
  brokerCreateOrderUrl,
  brokerOrderUrl,
  brokerOrderGroupUrl,
} from '../services/brokerApi';
import { searchPlatformInstruments, fetchPlatformTickerDetails } from '../services/platformInstruments';
import {
  recordOrderApiError,
  recordOrderApiResponse,
  startOrderLatency,
} from '../utils/orderLatencyRecorder';
import { parseCancelOrderFailure } from '../utils/orderResultUtils';
import { applyOrderReplaceResult, releaseReplaceCancelNotice, suppressReplaceCancelNotice } from '../utils/orderReplace';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Invalidate the historical orders query for an account so the newly created/modified
 * order shows up via REST as a backup to the orders WebSocket push. Connect Trade has a
 * brief propagation delay after order placement, so a single invalidation immediately
 * after the write can race ahead of the broker indexing the order - retry once, shortly
 * after, so the chart/table self-heal without requiring a manual page refresh.
 */
function invalidateOrdersQueries(queryClient, paperTrading, accountId) {
  if (!accountId) return;
  const predicate = (query) =>
    Array.isArray(query.queryKey) &&
    query.queryKey[0] === 'historicalOrders' &&
    query.queryKey[1]?.paperTrading === !!paperTrading &&
    query.queryKey[1]?.accountId === accountId;

  queryClient.invalidateQueries({ predicate });
  setTimeout(() => {
    queryClient.invalidateQueries({ predicate });
  }, 1500);
}

export const useGetTicker = () => {
    const queryClient = useQueryClient();
    const { request } = useAPI();
    const getTicker = useCallback(async (ticker) => {
        return await queryClient.fetchQuery({
            queryKey: queryKeys.tickerDetails(ticker),
            queryFn: async () => {
                // Import here to avoid circular dependencies
                const tickerDetailsCache = (await import('../utils/tickerDetailsCache')).default;
                
                // Check persistent cache first
                if (tickerDetailsCache.has(ticker)) {
                    const cachedDetails = tickerDetailsCache.get(ticker);
                    if (cachedDetails) {
                        // Return cached details in full response structure
                        return {
                            Symbols: [cachedDetails],
                            Errors: []
                        };
                    }
                }
                
                const response = await fetchPlatformTickerDetails(request, ticker);
                
                if (response && !response.Errors?.length && response.Symbols?.[0]) {
                    const tickerDetails = response.Symbols[0];
                    
                    // Cache in persistent storage (canonical symbol + input alias)
                    tickerDetailsCache.set(ticker, tickerDetails);
                    if (tickerDetails.Symbol && tickerDetails.Symbol !== ticker) {
                        tickerDetailsCache.set(tickerDetails.Symbol, tickerDetails);
                    }
                    
                    return response;
                }
                
                return response || { Symbols: [], Errors: [{ Message: 'Not found' }] };
            },
            staleTime: 24 * 60 * 60 * 1000, // 24 hours - ticker details rarely change
        });
    }, [queryClient, request]);
    return { getTicker };
}

export const useSuggestSymbols = () => {
    const { request } = useAPI();
    const suggestSymbols = useCallback(async (text) => {
        if (!text || text.trim().length === 0) return [];
        const trimmed = text.trim();
        try {
            return await searchPlatformInstruments(request, trimmed, { limit: 25 });
        } catch (_) {
            return [];
        }
    }, [request]);
    return { suggestSymbols };
};

export const useGetBars = () => {
    // Kept as thin wrapper around request for places that need direct fetch
    const getBars = useCallback(async (ticker, interval, unit, barsback, sessionTemplate) => {
        const url = `${BACKEND_URL}/tradestation/marketdata/barcharts/${ticker}?interval=${interval}&unit=${unit}&barsback=${barsback}&sessiontemplate=${encodeURIComponent(sessionTemplate)}`;
        const res = await fetch(url);
        return await res.json();
    }, []);
    return { getBars };
}

export const useGetAccounts = () => {
    // Prefer useAccountsQuery elsewhere
    const getAccounts = useCallback(() => ([]), []);
    return { getAccounts };
}

export const useGetAccountData = () => {
    // Replaced by dedicated React Query hooks
    const getAccountData = useCallback(async () => ({ balances: [], orders: [], positions: [] }), []);
    return { getAccountData };
};

export const useCreateOrder = () => {
    const { request } = useAPI();
    const queryClient = useQueryClient();
    const { setOrders } = useContext(TradingContext);
    const createOrder = useCallback(async (paperTrading, orderData) => {
        const firstOrder = orderData?.Orders?.[0] || orderData;
        const latencyId = startOrderLatency('create', {
            accountId: orderData?.AccountID || orderData?.accountId || firstOrder?.AccountID,
            symbol: firstOrder?.Symbol || firstOrder?.Legs?.[0]?.Symbol,
        });
        const url = brokerCreateOrderUrl(paperTrading);
        try {
            const result = await request(url, {}, 'POST', orderData);
            recordOrderApiResponse(latencyId, result);
            if (!result?.error && !result?.Error) {
                const accountId = orderData?.AccountID || orderData?.accountId;
                invalidateOrdersQueries(queryClient, paperTrading, accountId);
                const created = result?.Orders;
                if (Array.isArray(created) && created.length > 0 && setOrders) {
                    setOrders((prev) => {
                        const byId = new Map(prev.map((o) => [String(o.OrderID), o]));
                        for (const ord of created) {
                            if (!ord?.OrderID) continue;
                            const key = String(ord.OrderID);
                            byId.set(key, { ...(byId.get(key) || {}), ...ord });
                        }
                        return Array.from(byId.values());
                    });
                }
            }
            return result;
        } catch (error) {
            recordOrderApiError(latencyId, error);
            throw error;
        }
    }, [request, queryClient, setOrders]);
    return { createOrder };
}

export const useCancelOrder = () => {
    const { request } = useAPI();
    const queryClient = useQueryClient();
    const { setOrders } = useContext(TradingContext);
    const cancelOrder = useCallback(async (paperTrading, orderID, accountId = null) => {
        const latencyId = startOrderLatency('cancel', { orderId: orderID, accountId });
        const url = brokerOrderUrl(orderID, paperTrading, accountId);
        const key = String(orderID);
        let removedOrder = null;

        // Eagerly remove the order from state (this also removes its chart price line, since
        // both the orders table and StockChart's currentOrders are derived from this same
        // TradingContext.orders array) so cancellation feels instant instead of waiting on the
        // API round trip. Roll back by re-adding it if the cancel request fails.
        if (setOrders) {
            setOrders((prev) => {
                const idx = prev.findIndex((o) => String(o.OrderID) === key);
                if (idx === -1) return prev;
                removedOrder = prev[idx];
                return prev.filter((o) => String(o.OrderID) !== key);
            });
        }

        const restoreOnFailure = () => {
            if (!removedOrder || !setOrders) return;
            setOrders((prev) =>
                prev.some((o) => String(o.OrderID) === key) ? prev : [...prev, removedOrder]
            );
        };

        try {
            const result = await request(url, {}, 'DELETE', null, { error: 'Cancel order request failed' });
            recordOrderApiResponse(latencyId, result);
            const failure = parseCancelOrderFailure(result);
            if (failure) {
                restoreOnFailure();
            } else {
                invalidateOrdersQueries(queryClient, paperTrading, accountId);
            }
            return result;
        } catch (e) {
            recordOrderApiError(latencyId, e);
            restoreOnFailure();
            throw e;
        }
    }, [request, queryClient, setOrders]);
    return { cancelOrder };
}

export const useUpdateOrder = () => {
    const { request } = useAPI();
    const queryClient = useQueryClient();
    const { setOrders } = useContext(TradingContext);
    const updateOrder = useCallback(async (paperTrading, orderID, orderUpdateData, accountId = null) => {
        const latencyId = startOrderLatency('replace', {
            orderId: orderID,
            accountId,
            expected: {
                LimitPrice: orderUpdateData?.LimitPrice,
                StopPrice: orderUpdateData?.StopPrice,
            },
        });
        let url = brokerOrderUrl(orderID, paperTrading);
        if (accountId) url += `&accountId=${encodeURIComponent(accountId)}`;
        // A Connect Trade working order is cancelled and re-placed. The stream
        // cancel must not toast; a failed update releases the suppression.
        suppressReplaceCancelNotice(orderID);
        try {
            const result = await request(url, {}, 'PUT', orderUpdateData);
            recordOrderApiResponse(latencyId, result);
            const failed = result?.error || result?.Error;
            if (failed) releaseReplaceCancelNotice(orderID);
            if (setOrders && result && !failed) {
                setOrders((prev) => applyOrderReplaceResult(prev, orderID, result));
            }
            invalidateOrdersQueries(queryClient, paperTrading, accountId);
            return result;
        } catch (error) {
            releaseReplaceCancelNotice(orderID);
            recordOrderApiError(latencyId, error);
            throw error;
        }
    }, [request, queryClient, setOrders]);
    return { updateOrder };
}

export const useCreateBracketOrder = () => {
    const { request } = useAPI();
    const queryClient = useQueryClient();
    const createBracketOrder = useCallback(async (paperTrading, bracketOrderData) => {
        const url = brokerOrderGroupUrl(paperTrading);
        const result = await request(url, {}, 'POST', bracketOrderData);
        if (!result?.error && !result?.Error) {
            const accountId =
                bracketOrderData?.AccountID ||
                bracketOrderData?.accountId ||
                bracketOrderData?.Orders?.[0]?.AccountID;
            invalidateOrdersQueries(queryClient, paperTrading, accountId);
        }
        return result;
    }, [request, queryClient]);
    return { createBracketOrder };
}

export const useGetHistoricalOrders = () => {
    // Prefer useHistoricalOrdersInfiniteQuery elsewhere
    const getHistoricalOrders = useCallback(async () => ({ Orders: [], NextToken: null }), []);
    return { getHistoricalOrders };
}